<template>
  <div class="mall-orders" :class="{ 'is-embedded': isEmbedded }">
    <div class="mall-orders__shell">
      <header class="mall-orders__head">
        <div>
          <h1>주문내역</h1>
          <p class="mall-orders__lead">주문 상태와 배송을 확인합니다.</p>
        </div>
        <div v-if="unpaidPendingCount" class="mall-orders__remind">
          <p class="mall-orders__toast">
            <AppIcon icon="mdi-clock-outline" :size="18" />
            결제를 기다리는 주문이 {{ unpaidPendingCount }}건 있습니다.
            <template v-if="oldestUnpaidPendingHours >= 24">
              · 가장 오래된 건 {{ Math.floor(oldestUnpaidPendingHours / 24) }}일 경과
            </template>
            <template v-else-if="oldestUnpaidPendingHours >= 1">
              · 가장 오래된 건 {{ oldestUnpaidPendingHours }}시간 경과
            </template>
          </p>
          <div v-if="firstUnpaidPending?.payMethod === 'TOSS'" class="mall-orders__remind-box">
            <div>
              <strong>{{ formatPrice(firstUnpaidPending.totalAmount) }}원</strong>
              <span>카드·간편결제를 마쳐 주세요. 결제가 끝나면 발송이 시작됩니다.</span>
            </div>
            <div class="mall-orders__remind-actions">
              <router-link :to="`/mall/orders/${firstUnpaidPending.code}`">결제하기</router-link>
            </div>
          </div>
          <div v-else-if="payment && firstUnpaidPending" class="mall-orders__remind-box">
            <div>
              <strong>{{ formatPrice(firstUnpaidPending.totalAmount) }}원</strong>
              <span>
                {{ payment.bankName }} {{ payment.account }} · 예금주 {{ payment.holder }}
              </span>
              <em>입금자 {{ firstUnpaidPending.depositor || firstUnpaidPending.receiver }}</em>
            </div>
            <div class="mall-orders__remind-actions">
              <button
                type="button"
                @click="copyText(String(firstUnpaidPending.totalAmount), '입금 금액')"
              >
                금액 복사
              </button>
              <button type="button" @click="copyText(payment.account.replace(/\s/g, ''), '계좌번호')">
                계좌 복사
              </button>
              <router-link :to="`/mall/orders/${firstUnpaidPending.code}`">입금 안내</router-link>
            </div>
          </div>
        </div>
        <div v-else-if="notifiedPendingCount" class="mall-orders__remind is-confirming">
          <p class="mall-orders__toast">
            <AppIcon icon="mdi-check-circle-outline" :size="18" />
            입금 확인을 기다리는 주문이 {{ notifiedPendingCount }}건 있습니다.
          </p>
          <div v-if="firstNotifiedPending" class="mall-orders__remind-box">
            <div>
              <strong>{{ formatPrice(firstNotifiedPending.totalAmount) }}원</strong>
              <span>「입금했어요」 알림을 보냈습니다. 운영팀이 통장을 확인하면 배송이 시작됩니다.</span>
              <em v-if="firstNotifiedPending.depositor">
                입금자 {{ firstNotifiedPending.depositor }}
              </em>
            </div>
            <div class="mall-orders__remind-actions">
              <router-link :to="`/mall/orders/${firstNotifiedPending.code}`">주문 상세</router-link>
            </div>
          </div>
        </div>
      </header>

      <MallMyFilterBar
        class="mall-orders__filter"
        :from-date="fromDate"
        :to-date="toDate"
        :keyword="keyword"
        :preset="preset"
        placeholder="주문번호·상품명"
        @update:from-date="onFromDate"
        @update:to-date="onToDate"
        @update:keyword="keyword = $event"
        @preset="onPreset"
        @search="reload"
      />

      <div v-if="!loading && orders.length" class="mall-orders__tabs" role="tablist">
        <button
          v-for="tab in filterTabs"
          :key="tab.key"
          type="button"
          role="tab"
          class="mall-orders__tab"
          :class="{ 'is-active': filterKey === tab.key }"
          :aria-selected="filterKey === tab.key"
          @click="filterKey = tab.key"
        >
          {{ tab.label }}
          <em>{{ tab.count }}</em>
        </button>
      </div>

      <div v-if="loading" class="mall-orders__empty">
        <AppIcon icon="mdi-loading" :size="40" />
        <p>주문을 불러오는 중입니다…</p>
      </div>

      <div v-else-if="!orders.length" class="mall-orders__empty">
        <AppIcon icon="mdi-clipboard-text-off-outline" :size="56" />
        <p>주문내역이 없습니다.</p>
        <router-link to="/mall">쇼핑계속하기</router-link>
      </div>

      <div v-else-if="!filteredOrders.length" class="mall-orders__empty">
        <AppIcon icon="mdi-filter-off-outline" :size="48" />
        <p>이 상태에 해당하는 주문이 없습니다.</p>
        <button type="button" class="mall-orders__reset" @click="filterKey = 'ALL'">전체 보기</button>
      </div>

      <ul v-else class="mall-orders__list">
        <li
          v-for="order in filteredOrders"
          :key="order.code"
          class="mall-orders__card"
          :class="{
            'is-unpaid': order.status === 'PENDING' && !order.payNotifiedAt,
            'is-confirming': order.status === 'PENDING' && Boolean(order.payNotifiedAt),
          }"
        >
          <header>
            <div>
              <strong>{{ order.code }}</strong>
              <time>{{ formatDate(order.orderedAt) }}</time>
            </div>
            <span
              class="mall-orders__status"
              :class="buyerStatusClass(order)"
            >
              {{ buyerStatusLabel(order) }}
            </span>
          </header>

          <p
            v-if="order.status === 'PENDING'"
            class="mall-orders__pending-note"
            :class="{ 'is-confirming': Boolean(order.payNotifiedAt) }"
          >
            <AppIcon
              :icon="order.payNotifiedAt ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"
              :size="16"
            />
            <template v-if="order.payMethod === 'TOSS'">
              {{ pendingAgeLabel(order.orderedAt) }} · 카드·간편결제를 마치지 않으면 자동 취소됩니다
            </template>
            <template v-else-if="order.payNotifiedAt">
              입금 알림을 보냈습니다. 운영팀 확인을 기다립니다.
            </template>
            <template v-else>
              {{ pendingAgeLabel(order.orderedAt) }} · 이체 후 「입금했어요」를 누르지 않으면 자동 취소됩니다
            </template>
          </p>

          <p class="mall-orders__summary">
            {{ order.itemSummary || '주문 상품' }}
            <em v-if="order.itemCount > 1">외 {{ order.itemCount - 1 }}건</em>
          </p>

          <div v-if="order.status === 'PAID' && shippingSummary(order)" class="mall-orders__ship">
            <span
              v-for="badge in shippingSummary(order)"
              :key="badge.key"
              class="mall-orders__ship-badge"
              :class="badge.tone"
            >
              {{ badge.label }}
              <em v-if="badge.count > 1">{{ badge.count }}</em>
            </span>
          </div>

          <div v-if="trackItemsOf(order).length" class="mall-orders__tracks">
            <button
              v-for="item in trackItemsOf(order)"
              :key="item.idx"
              type="button"
              class="mall-orders__track"
              @click="openTracking({ ...item, orderCode: order.code })"
            >
              <AppIcon icon="mdi-truck-outline" :size="16" />
              <span>{{ item.courier || '택배' }} {{ item.invoice }}</span>
              배송조회
            </button>
          </div>

          <footer>
            <div>
              <span>{{ order.receiver }} · {{ order.phone }}</span>
              <p>{{ order.address }} {{ order.addressDetail }}</p>
            </div>
            <div class="mall-orders__foot-right">
              <strong>{{ formatPrice(order.totalAmount) }}원</strong>
              <router-link :to="`/mall/orders/${order.code}`" class="mall-orders__detail">
                {{
                  order.status === 'PENDING' && order.payMethod === 'TOSS'
                    ? '결제하기'
                    : order.status === 'PENDING' && !order.payNotifiedAt
                      ? '입금 안내 보기'
                      : '주문 상세'
                }}
              </router-link>
            </div>
          </footer>
        </li>
      </ul>
    </div>

    <MallTrackingModal
      v-if="trackTarget"
      :item-idx="trackTarget.idx"
      :courier="trackTarget.courier"
      :invoice="trackTarget.invoice"
      :product-name="trackTarget.name"
      :order-code="trackTarget.orderCode"
      @close="onCloseTracking"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import MallTrackingModal from '@/components/mall/MallTrackingModal.vue'
import MallMyFilterBar from '@/components/mall/MallMyFilterBar.vue'
import { useListDateRange } from '@/composables/useListDateRange'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMallToast } from '@/composables/useMallToast'
import { formatPrice } from '@/constants/mallProducts'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  MALL_ORDER_STATUS_LABEL,
  mallBuyerOrderStatusLabel,
  fetchMyOrders,
  fetchPaymentInfo,
  type MallOrder,
  type MallOrderItemStatus,
  type MallOrderStatus,
  type MallPaymentInfo,
} from '@/api/mallOrder'
import { useMallTracking } from '@/composables/useMallTracking'
import { copyToClipboard } from '@/utils/clipboard'
import type { DatePresetKey } from '@/utils/dateInput'

type FilterKey = 'ALL' | MallOrderStatus | 'READY' | 'SHIPPING' | 'DELIVERED' | 'CLAIM'

const isEmbedded = useMallMypageEmbedded()
const { showSuccess, showError } = useMallToast()
const { trackTarget, openTracking, closeTracking } = useMallTracking()

function trackItemsOf(order: MallOrder) {
  return order.trackItems || []
}

function onCloseTracking() {
  closeTracking()
  void reload()
}
const { fromDate, toDate, preset, applyPreset, inRange } = useListDateRange('3m')

const orders = ref<MallOrder[]>([])
const payment = ref<MallPaymentInfo | null>(null)
const loading = ref(true)
const filterKey = ref<FilterKey>('ALL')
const keyword = ref('')

function onFromDate(value: string) {
  fromDate.value = value
  preset.value = 'all'
}

function onToDate(value: string) {
  toDate.value = value
  preset.value = 'all'
}

function onPreset(key: DatePresetKey) {
  applyPreset(key)
  void reload()
}

async function reload() {
  loading.value = true
  try {
    const [list, pay] = await Promise.all([
      fetchMyOrders({
        from: fromDate.value || undefined,
        to: toDate.value || undefined,
        keyword: keyword.value.trim() || undefined,
      }),
      fetchPaymentInfo().catch(() => null),
    ])
    orders.value = list
    payment.value = pay
  } catch {
    orders.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void reload()
})

const pendingOrders = computed(() => orders.value.filter((order) => order.status === 'PENDING'))
const unpaidPendingOrders = computed(() =>
  pendingOrders.value.filter((order) => !order.payNotifiedAt),
)
const notifiedPendingOrders = computed(() =>
  pendingOrders.value.filter((order) => Boolean(order.payNotifiedAt)),
)
const unpaidPendingCount = computed(() => unpaidPendingOrders.value.length)
const notifiedPendingCount = computed(() => notifiedPendingOrders.value.length)
const firstUnpaidPending = computed(() => unpaidPendingOrders.value[0] || null)
const firstNotifiedPending = computed(() => notifiedPendingOrders.value[0] || null)

const oldestUnpaidPendingHours = computed(() => {
  if (!unpaidPendingOrders.value.length) return 0
  const oldest = Math.min(
    ...unpaidPendingOrders.value.map((order) => new Date(order.orderedAt).getTime()),
  )
  return Math.max(0, Math.floor((Date.now() - oldest) / 3600000))
})

function pendingAgeLabel(orderedAt: string) {
  const hours = Math.max(0, Math.floor((Date.now() - new Date(orderedAt).getTime()) / 3600000))
  if (hours >= 48) return `주문 후 ${Math.floor(hours / 24)}일 경과`
  if (hours >= 1) return `주문 후 ${hours}시간 경과`
  return '방금 주문'
}

async function copyText(value: string, label: string) {
  const ok = await copyToClipboard(value)
  if (ok) {
    showSuccess(`${label}을(를) 복사했습니다.`)
    return
  }
  showError(`${label} 복사에 실패했습니다. 직접 선택해 복사해 주세요.`)
}

function matchesFilter(order: MallOrder, key: FilterKey) {
  if (key === 'ALL') return true
  if (key === 'PENDING' || key === 'CANCELED') {
    return order.status === key
  }
  if (key === 'PAID') {
    /* 결제만 되고 아직 발송준비·배송 전이거나, 입금확인 직후 */
    if (order.status !== 'PAID') return false
    const statuses = order.itemStatuses || []
    if (!statuses.length) return true
    return statuses.some((status) => status === 'PAID' || status === 'WAITING')
  }
  if (key === 'READY') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) => status === 'READY')
    )
  }
  if (key === 'SHIPPING') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) => status === 'SHIPPING')
    )
  }
  if (key === 'DELIVERED') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) =>
        status === 'DELIVERED' || status === 'DONE',
      )
    )
  }
  if (key === 'CLAIM') {
    return (order.itemStatuses || []).some((status) =>
      ['CANCEL_REQUEST', 'RETURN_REQUEST', 'CANCELED'].includes(status),
    )
  }
  return true
}

/** 주문 카드 오른쪽 배지. 입금 확인 뒤에는 상품 진행 상태를 보여 준다. */
function buyerStatusLabel(order: MallOrder) {
  if (order.status !== 'PAID') {
    return mallBuyerOrderStatusLabel(order)
  }
  const statuses = (order.itemStatuses || []).filter((status) => status !== 'CANCELED')
  if (!statuses.length) return MALL_ORDER_STATUS_LABEL.PAID
  if (statuses.some((status) => status === 'SHIPPING')) return MALL_ORDER_ITEM_STATUS_LABEL.SHIPPING
  if (statuses.some((status) => status === 'READY')) return MALL_ORDER_ITEM_STATUS_LABEL.READY
  if (statuses.some((status) => status === 'DELIVERED')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.DELIVERED
  }
  if (statuses.every((status) => status === 'DONE')) return MALL_ORDER_ITEM_STATUS_LABEL.DONE
  if (statuses.some((status) => status === 'CANCEL_REQUEST')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.CANCEL_REQUEST
  }
  if (statuses.some((status) => status === 'RETURN_REQUEST')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.RETURN_REQUEST
  }
  return MALL_ORDER_STATUS_LABEL.PAID
}

function buyerStatusClass(order: MallOrder) {
  if (order.status === 'PENDING' && order.payNotifiedAt) return 'is-confirming'
  if (order.status === 'PENDING') return 'is-pending'
  if (order.status === 'CANCELED') return 'is-canceled'
  const label = buyerStatusLabel(order)
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.SHIPPING) return 'is-shipping'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.READY) return 'is-ready'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.DELIVERED) return 'is-delivered'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.DONE) return 'is-done'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.CANCEL_REQUEST) return 'is-claim'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.RETURN_REQUEST) return 'is-claim'
  return 'is-paid'
}

const filterTabs = computed(() => {
  const tabs: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: 'ALL', label: '전체', count: orders.value.length },
    {
      key: 'PENDING',
      label: '결제대기',
      count: orders.value.filter((order) => matchesFilter(order, 'PENDING')).length,
    },
    {
      key: 'PAID',
      label: '결제완료',
      count: orders.value.filter((order) => matchesFilter(order, 'PAID')).length,
    },
    {
      key: 'READY',
      label: '발송준비',
      count: orders.value.filter((order) => matchesFilter(order, 'READY')).length,
    },
    {
      key: 'SHIPPING',
      label: '배송중',
      count: orders.value.filter((order) => matchesFilter(order, 'SHIPPING')).length,
    },
    {
      key: 'DELIVERED',
      label: '배송완료',
      count: orders.value.filter((order) => matchesFilter(order, 'DELIVERED')).length,
    },
    {
      key: 'CLAIM',
      label: '취소·반품',
      count: orders.value.filter((order) => matchesFilter(order, 'CLAIM')).length,
    },
  ]
  return tabs
})

const filteredOrders = computed(() =>
  orders.value.filter((order) => {
    if (!matchesFilter(order, filterKey.value)) return false
    if (!inRange(order.orderedAt)) return false
    const q = keyword.value.trim().toLowerCase()
    if (!q) return true
    const hay = `${order.code} ${order.itemSummary || ''} ${order.receiver || ''}`.toLowerCase()
    return hay.includes(q)
  }),
)

const STATUS_ORDER: MallOrderItemStatus[] = [
  'CANCEL_REQUEST',
  'RETURN_REQUEST',
  'PAID',
  'READY',
  'SHIPPING',
  'DELIVERED',
  'DONE',
  'WAITING',
  'CANCELED',
]

function shippingSummary(order: MallOrder) {
  const statuses = order.itemStatuses || []
  if (!statuses.length) return []

  const counts = statuses.reduce<Partial<Record<MallOrderItemStatus, number>>>((acc, status) => {
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {})

  return STATUS_ORDER.filter((key) => counts[key])
    .map((key) => ({
      key,
      label: MALL_ORDER_ITEM_STATUS_LABEL[key],
      count: counts[key] || 0,
      tone: `is-${key.toLowerCase()}`,
    }))
    .slice(0, 4)
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<style lang="scss" src="./MallOrdersPage.scss"></style>
